// src/pages/Appli/AppliAdmin/TestimonialsAdmin/CreateTestimonialModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { addTestimonial } from "./../../../../services/testimonialServices";
import { useNotification } from "./../../../../components/Notification/NotificationManager";

const normLang = (l) => {
  const v = String(l || "fr").toLowerCase();
  if (v.startsWith("fr")) return "fr";
  if (v.startsWith("en")) return "en";
  return "fr";
};

export default function CreateTestimonialModal({ onClose, onCreate, language }) {
  const { addNotification } = useNotification();
  const lang = useMemo(() => normLang(language), [language]);

  const [formData, setFormData] = useState(() => ({
    ordre: "",
    testimonial_date: new Date().toISOString().split("T")[0],
    is_active: true,
    [`testimonial_title_${lang}`]: "",
    [`testimonial_content_${lang}`]: "",
  }));

  // reset propre si la langue change pendant que la modal est ouverte
  useEffect(() => {
    setFormData((prev) => ({
      ordre: prev.ordre ?? "",
      testimonial_date: prev.testimonial_date ?? new Date().toISOString().split("T")[0],
      is_active: prev.is_active ?? true,
      [`testimonial_title_${lang}`]: prev[`testimonial_title_${lang}`] ?? "",
      [`testimonial_content_${lang}`]: prev[`testimonial_content_${lang}`] ?? "",
    }));
  }, [lang]);

  const titleKey = `testimonial_title_${lang}`;
  const contentKey = `testimonial_content_${lang}`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, [contentKey]: value }));
  };

  const handleSubmit = async () => {
    const title = String(formData[titleKey] || "").trim();
    const content = String(formData[contentKey] || "").trim();

    if (!title || !content) {
      addNotification("Titre et contenu obligatoires.", "error");
      return;
    }

    try {
      const payload = {
        [titleKey]: title,
        [contentKey]: content,
        ordre: formData.ordre !== "" ? Number(formData.ordre) : 0,
        testimonial_date: formData.testimonial_date,
        is_active: !!formData.is_active,
      };

      await addTestimonial(payload);
      addNotification("Témoignage créé avec succès.", "success");
      onCreate?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la création du témoignage :", error);
      addNotification("Erreur lors de la création du témoignage.", "error");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4">
      <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-black/10 dark:border-white/10">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">
            Créer un témoignage
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Langue : <span className="font-bold">{lang.toUpperCase()}</span>
          </p>
        </div>

        <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Fonction / Titre ({lang.toUpperCase()})
            </label>
            <input
              type="text"
              name={titleKey}
              value={formData[titleKey] || ""}
              onChange={handleChange}
              placeholder="Ex: Président, Directeur, Responsable…"
              className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-black/20 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Contenu ({lang.toUpperCase()})
            </label>
            <div className="rounded-3xl border border-black/10 dark:border-white/10 overflow-hidden bg-white dark:bg-black/20">
              <ReactQuill
                theme="snow"
                value={formData[contentKey] || ""}
                onChange={handleContentChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Ordre
              </label>
              <input
                type="number"
                name="ordre"
                value={formData.ordre}
                onChange={handleChange}
                className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-black/20 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Date
              </label>
              <input
                type="date"
                name="testimonial_date"
                value={formData.testimonial_date}
                onChange={handleChange}
                className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-black/20 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex items-end">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!formData.is_active}
                  onChange={handleChange}
                  className="h-5 w-5 accent-primary"
                />
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Actif
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-black/10 dark:border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-bold border border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/20 text-gray-900 dark:text-white hover:bg-white dark:hover:bg-black/30 transition"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            className="rounded-2xl px-4 py-2 text-sm font-extrabold bg-primary text-white hover:opacity-95 transition"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
